import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { initializeApp } from "firebase/app";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import { FaUser, FaAddressCard, FaPhone, FaCalendar, FaEnvelope, FaExclamationTriangle, FaSyncAlt, FaMapMarkerAlt, FaReceipt, FaShoppingCart, FaLock } from "react-icons/fa";
import { clearCart, removeFromCart } from "./CartReducer";
import IntegratedHeader from "./Headercomponent";

// Initialize Firebase
const firebaseConfig = {
  apiKey: "AIzaSyDBnzIitbmRBp1o-RpA-uYLUDr3tWtdK1Y",
  authDomain: "lihen-pharmacy.firebaseapp.com",
  projectId: "lihen-pharmacy",
  storageBucket: "lihen-pharmacy.appspot.com",
  messagingSenderId: "546291727469",
  appId: "1:546291727469:web:3b1aaff3f11e0877e2d364",
  measurementId: "G-YXF7FF6CR9"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Store location coordinates (Big Taste, Ekhei girls road Auchi)
const STORE_LOCATION = {
  latitude: 7.059550952987406,
  longitude: 6.262928301143551,
};

// Detect if user is on a mobile device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const CheckoutScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Access cart from Redux store with proper fallback
  const cart = useSelector((state) => state.cart?.cart || []);

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    address: "",
    phoneNumber: "",
    email: "",
    age: ""
  });
  const [phoneError, setPhoneError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [firebaseError, setFirebaseError] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [distance, setDistance] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [deliveryFeeError, setDeliveryFeeError] = useState(null);
  const [isFetchingDeliveryFee, setIsFetchingDeliveryFee] = useState(true);
  const [inputFocus, setInputFocus] = useState({
    fullName: false,
    address: false,
    phoneNumber: false,
    age: false,
    email: false
  });
  const [transactionRef, setTransactionRef] = useState('');
  const [locationError, setLocationError] = useState(null);
  const [showPcWarning, setShowPcWarning] = useState(false);

  useEffect(() => {
    // Generate transaction reference when component mounts
    setTransactionRef(generateTransactionRef(10));
    getUserLocation();
    
    // Show warning if user is on a PC browser
    if (!isMobileDevice()) {
      setShowPcWarning(true);
    }
  }, []);

  // Calculate cart totals with proper fallbacks
  const totalAmount = cart.reduce((sum, item) => {
    const price = typeof item.Price === 'string' ? parseFloat(item.Price) : item.Price || 0;
    const quantity = item.quantity || 0;
    return sum + (price * quantity);
  }, 0);

  // Check if current time is between 6pm and 7:59pm for evening surcharge
  const isEveningSurchargeTime = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    return (currentHour === 18 && currentMinute >= 0) || 
           (currentHour === 19 && currentMinute <= 59);
  };

  const eveningSurcharge = isEveningSurchargeTime() ? 300 : 0;

  // ✅ Only calculate finalAmount if deliveryFee is properly loaded without errors
  const finalAmount =
    deliveryFee !== null && deliveryFeeError === null
      ? totalAmount + deliveryFee + eveningSurcharge
      : null;

  // Handle input focus
  const handleFocus = (field) => {
    setInputFocus(prev => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setInputFocus(prev => ({ ...prev, [field]: false }));
  };

  // Calculate distance between two coordinates (Haversine formula) - FIXED
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    // Convert degrees to radians
    const toRadians = (degree) => degree * (Math.PI / 180);
    
    const R = 6371; // Earth's radius in kilometers
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);
    
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in kilometers
  };

  // Get user location using browser geolocation API - IMPROVED
  const getUserLocation = () => {
    setLocationLoading(true);
    setLocationError(null);
    setDeliveryFeeError(null);
    
    if (!navigator.geolocation) {
      const errorMsg = "Geolocation is not supported by your browser";
      setLocationError(errorMsg);
      setLocationLoading(false);
      // Set a default delivery fee if location can't be obtained
      // Do not allow default fee if location can't be obtained
        setDeliveryFee(null);
        setDeliveryFeeError("Unable to determine delivery fee. Please enable location.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const calculatedDistance = calculateDistance(
          latitude, 
          longitude, 
          STORE_LOCATION.latitude, 
          STORE_LOCATION.longitude
        );
        
        setDistance(calculatedDistance);
        getDeliveryFeeBasedOnDistance(calculatedDistance);
        setLocationLoading(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        let errorMsg = "Unable to get your location.";
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = "Location access denied. Please enable location permissions in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMsg = "Location request timed out. Please try again.";
            break;
          default:
            errorMsg = "An unknown error occurred while getting your location.";
        }
        
        setLocationError(errorMsg);
        setLocationLoading(false);
        setDeliveryFee(null);
        setDeliveryFeeError("Unable to calculate delivery fee without distance and location.");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  // Get delivery fee based on distance - FIXED
  const getDeliveryFeeBasedOnDistance = async (distance) => {
    try {
      setIsFetchingDeliveryFee(true);
      setDeliveryFeeError(null);
      
      // Validate distance
      if (distance === null || isNaN(distance)) {
        console.error("Invalid distance value:", distance);
        setDeliveryFee(0);
        setDeliveryFeeError("Invalid distance calculation. Please try again.");
        return;
      }

      let feeDocRef;
      let feeField;

      if (distance <= 0.5) {
        feeDocRef = doc(db, 'DeliveryFee', 'DeliveryFee00');
        feeField = 'DeliveryFee00';
      } else if (distance > 0.5 && distance < 1) {
        feeDocRef = doc(db, 'DeliveryFee', 'DeliveryFee0');
        feeField = 'DeliveryFee0';
      } else if (distance >= 1 && distance <= 3) {
        feeDocRef = doc(db, 'DeliveryFee', 'DeliveryFee');
        feeField = 'DeliveryFee';
      } else if (distance > 3 && distance <= 6) {
        feeDocRef = doc(db, 'DeliveryFee', 'DeliveryFee2');
        feeField = 'DeliveryFee2';
      } else if (distance > 6 && distance <= 10) {
        feeDocRef = doc(db, 'DeliveryFee', 'DeliveryFee3');
        feeField = 'DeliveryFee3';
      } else {
        feeDocRef = doc(db, 'DeliveryFee', 'DeliveryFee3');
        feeField = 'DeliveryFee3';
      }

      const deliveryFeeDoc = await getDoc(feeDocRef);
      
      if (deliveryFeeDoc.exists()) {
        const baseFee = deliveryFeeDoc.data()[feeField] || 0;
        let finalFee = baseFee;
        
        if (distance > 10) {
          const surcharge = Math.floor((distance - 10) * 150);
          finalFee = baseFee + surcharge;
        }
        
        setDeliveryFee(finalFee);
        setDeliveryFeeError(null);
      } else {
        console.log('Delivery fee document does not exist.');
        setDeliveryFee(0);
        setDeliveryFeeError("Delivery fee not configured. Please contact support.");
      }
    } catch (error) {
      console.error('Error fetching delivery fee:', error);
      setDeliveryFee(0);
      setDeliveryFeeError("Unable to calculate delivery fee. Please try again.");
    } finally {
      setIsFetchingDeliveryFee(false);
    }
  };

  // Generate transaction reference
  const generateTransactionRef = (length = 10) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    // Get current time in GMT+1
    const now = new Date();
    const gmtPlus1 = new Date(now.getTime() + 60 * 60 * 1000);

    // Format date as YYYYMMDD
    const year = gmtPlus1.getFullYear();
    const month = String(gmtPlus1.getMonth() + 1).padStart(2, '0');
    const day = String(gmtPlus1.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;

    return `LPW${dateStr}${gmtPlus1.getTime()}${result}`;
  };

  // Handle payment success
  const handlePaymentSuccess = async (response) => {
    setIsLoading(true);
    setFirebaseError(null);
    
    try {
      const orderData = {
        fullName: formData.fullName || "N/A",
        address: formData.address || "N/A",
        phoneNumber: formData.phoneNumber || "N/A",
        email: formData.email || "N/A",
        age: formData.age || "N/A",
        cartItems: cart.map(item => ({
          productId: item.ID || "N/A",
          productName: item.ID || "Product",
          quantity: item.quantity || 0,
          amount: (item.Price || 0) * (item.quantity || 0),
        })),
        deliveryFee: deliveryFee,
        distance: distance,
        finalAmount: finalAmount,
        transactionRef: transactionRef,
        eveningSurcharge: eveningSurcharge,
        flutterwaveTransactionId: response.transaction_id || "N/A",
        timestamp: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, "orders"), orderData);
      
      // Clear cart
      cart.forEach((item) => {
        dispatch(removeFromCart({ ID: item.ID }));
      });

      navigate("/Thankyou", {
        state: {
          orderId: docRef.id,
          transactionRef: transactionRef,
          customerName: formData.fullName,
          orderTimestamp: new Date().toISOString(),
          totalAmount: finalAmount,
          deliveryAddress: formData.address,
          deliveryFee: deliveryFee,
          distance: distance,
          eveningSurcharge: eveningSurcharge,
          subtotal: totalAmount,
          estimatedDelivery: new Date(Date.now() + 30 * 60 * 1000).toLocaleString(),
          cartItems: cart.map(item => ({
            productName: item.ID || 'Product',
            quantity: item.quantity || 1,
            amount: (item.Price || 0) * (item.quantity || 1)
          }))
        }
      });

    } catch (error) {
      console.error("Error writing to Firebase:", error);
      setFirebaseError("Failed to save order. Please contact support.");
      setIsLoading(false);
    } finally {
      closePaymentModal();
    }
  };

  // Handle payment error
  const handlePaymentError = () => {
    console.log("Payment failed or was closed");
    setIsLoading(false);
    alert("Payment canceled or failed. Please try again.");
  };

  // Flutterwave config
  const config = {
    public_key: "FLWPUBK-6567063421b625d2f24704dcb89d7edc-X",
    tx_ref: transactionRef,
    amount: finalAmount,
    currency: "NGN",
    payment_options: "card, banktransfer",
    customer: {
      email: formData.email || "no-email@example.com",
      phonenumber: formData.phoneNumber,
      name: formData.fullName
    },
    customizations: {
      title: "Lihen Pharmacy",
      description: `Payment for ${cart.length} items`,
      logo: "https://i.postimg.cc/zvYgLfzd/Blue-White-Illustration-Pharmacy-logo-20.png"
    }
  };

  const handleFlutterwavePayment = useFlutterwave(config);

  // Form validation
  const isValidPhoneNumber = (phone) => {
    const phonePattern = /^[0-9]{11}$/;
    return phonePattern.test(phone);
  };

  const isFormValid = () => {
    return (
      formData.fullName &&
      formData.address &&
      isValidPhoneNumber(formData.phoneNumber) &&
      formData.age &&
      cart.length > 0 &&
      deliveryFee !== null
    );
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!isFormValid()) {
      if (!formData.fullName || !formData.address || !formData.age) {
        alert("Please fill in all required fields: Name, Address, and Age.");
      } else if (!isValidPhoneNumber(formData.phoneNumber)) {
        alert("Please enter a valid 11-digit phone number.");
      } else if (deliveryFee === null) {
        alert("Delivery fee not loaded. Please wait or reload the page.");
      } else if (cart.length === 0) {
        alert("Your cart is empty. Please add items before checkout.");
      }
      return;
    }
    
    setIsLoading(true);
    handleFlutterwavePayment({
      callback: handlePaymentSuccess,
      onClose: handlePaymentError
    });
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === "phoneNumber") {
      if (value.length > 11) return;
      setPhoneError(isValidPhoneNumber(value) ? "" : "Please enter a valid 11-digit phone number");
    }
  };

  // Reload delivery fee
  const reloadDeliveryFee = async () => {
    setIsFetchingDeliveryFee(true);
    setDeliveryFeeError(null);
    getUserLocation();
  };

  // Render cart items for receipt
  const renderReceiptItem = (item, index) => {
    const price = typeof item.Price === 'string' ? parseFloat(item.Price) : item.Price || 0;
    const quantity = item.quantity || 0;
    
    return (
      <div key={index} style={styles.receiptItemRow}>
        <div style={styles.receiptProductName}>
          {item.ID || 'Unnamed Product'}
        </div>
        <div style={styles.receiptQuantity}>{quantity}</div>
        <div style={styles.receiptUnitPrice}>₦{price.toLocaleString()}</div>
        <div style={styles.receiptTotal}>₦{(price * quantity).toLocaleString()}</div>
      </div>
    );
  };

  if (!cart || cart.length === 0) {
    return (
      <div style={styles.container}>
        <IntegratedHeader />
        <div style={styles.emptyContainer}>
          <div style={styles.emptyIcon}>🛒</div>
          <h2 style={styles.emptyTitle}>No Items in Cart</h2>
          <p style={styles.emptyText}>Please add items to proceed to checkout</p>
          <button 
            style={styles.backButton}
            onClick={() => navigate('/')}
          >
            Back to Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <IntegratedHeader />
      
      {/* PC Browser Warning Alert */}
      {showPcWarning && (
        <div style={styles.pcWarning}>
          <div style={styles.pcWarningContent}>
            <FaExclamationTriangle style={styles.pcWarningIcon} />
            <div style={styles.pcWarningText}>
              <strong>PC Browser Detected</strong>
              <p>Delivery fees are calculated based on your location. If the fee seems unusual, please access this website on your mobile device for more accurate location detection.</p>
            </div>
            <button 
              style={styles.pcWarningClose}
              onClick={() => setShowPcWarning(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div style={styles.content}>
        {/* Delivery Information Form */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <FaMapMarkerAlt style={styles.sectionIcon} />
            <h2 style={styles.sectionTitle}>Delivery Information</h2>
          </div>
          
          <div style={styles.deliveryBox}>
            <div style={{
              ...styles.inputGroup,
              ...(inputFocus.fullName ? styles.inputGroupFocused : {})
            }}>
              <FaUser style={styles.inputIcon} />
              <input
                style={styles.input}
                placeholder="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                onFocus={() => handleFocus('fullName')}
                onBlur={() => handleBlur('fullName')}
                required
              />
            </div>
            
            <div style={{
              ...styles.inputGroup,
              ...(inputFocus.address ? styles.inputGroupFocused : {})
            }}>
              <FaAddressCard style={styles.inputIcon} />
              <input
                style={styles.input}
                placeholder="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                onFocus={() => handleFocus('address')}
                onBlur={() => handleBlur('address')}
                required
              />
            </div>
            
            <div style={{
              ...styles.inputGroup,
              ...(inputFocus.phoneNumber ? styles.inputGroupFocused : {})
            }}>
              <FaPhone style={{ ...styles.inputIcon, transform: "rotate(90deg)" }} />
              <input
                style={styles.input}
                placeholder="Phone Number (11 digits)"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                maxLength={11}
                type="tel"
                pattern="[0-9]{11}"
                onFocus={() => handleFocus('phoneNumber')}
                onBlur={() => handleBlur('phoneNumber')}
                required
              />
            </div>
            {phoneError && <p style={styles.errorText}>{phoneError}</p>}
            
            <div style={{
              ...styles.inputGroup,
              ...(inputFocus.age ? styles.inputGroupFocused : {})
            }}>
              <FaCalendar style={styles.inputIcon} />
              <input
                style={styles.input}
                placeholder="Age (e.g., 25 years, 3 months)"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                onFocus={() => handleFocus('age')}
                onBlur={() => handleBlur('age')}
                required
              />
            </div>
            
            <div style={{
              ...styles.inputGroup,
              ...(inputFocus.email ? styles.inputGroupFocused : {})
            }}>
              <FaEnvelope style={styles.inputIcon} />
              <input
                style={styles.input}
                placeholder="Email Address (Optional)"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                type="email"
                onFocus={() => handleFocus('email')}
                onBlur={() => handleBlur('email')}
              />
            </div>
          </div>
        </div>

        {/* Distance Information */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <FaMapMarkerAlt style={styles.sectionIcon} />
            <h2 style={styles.sectionTitle}>Delivery Distance</h2>
          </div>
          
          {locationLoading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={styles.loadingText}>Calculating distance...</p>
            </div>
          ) : locationError ? (
            <div style={styles.errorBox}>
              <FaExclamationTriangle style={styles.errorIcon} />
              <div style={styles.errorContent}>
                <p style={styles.errorMessage}>{locationError}</p>
              </div>
              <button 
                onClick={getUserLocation}
                style={styles.reloadButton}
              >
                <FaSyncAlt style={styles.reloadIcon} />
                Retry
              </button>
            </div>
          ) : distance !== null ? (
            <div style={styles.distanceContainer}>
              <FaMapMarkerAlt style={styles.distanceIcon} />
              <p style={styles.distanceText}>
                You are <span style={styles.distanceValue}>{distance.toFixed(1)} km</span> away
              </p>
            </div>
          ) : (
            <button style={styles.retryButton} onClick={getUserLocation}>
              <FaSyncAlt style={styles.retryIcon} />
              Retry Location Detection
            </button>
          )}
        </div>

        {/* Order Summary Section */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <FaReceipt style={styles.sectionIcon} />
            <h2 style={styles.sectionTitle}>Order Summary</h2>
          </div>
          
          <div style={styles.summaryRow}>
            <span>Items ({cart.length})</span>
            <span>₦{totalAmount.toLocaleString()}</span>
          </div>
          
          {isFetchingDeliveryFee ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={styles.loadingText}>Calculating delivery fee...</p>
            </div>
          ) : deliveryFee !== null ? (
            <>
              <div style={styles.summaryRow}>
                <span>Delivery Fee ({distance ? distance.toFixed(1) + ' km' : 'N/A'})</span>
                <span>₦{deliveryFee.toLocaleString()}</span>
              </div>
              
              {distance > 10 && (
                <div style={styles.summaryNote}>
                  * Includes ₦{Math.floor((distance - 10) * 150).toLocaleString()} surcharge for additional distance
                </div>
              )}
              
              {/* Evening Surcharge (6pm-7:59pm) */}
              {isEveningSurchargeTime() && (
                <div style={styles.summaryRow}>
                  <span>Evening Delivery Extra Charge</span>
                  <span>₦300</span>
                </div>
              )}
              
              <div style={styles.divider}></div>
              
              <div style={styles.finalSummaryRow}>
                <strong>Total Amount</strong>
                <strong>₦{finalAmount.toLocaleString()}</strong>
              </div>
            </>
          ) : (
            <div style={styles.errorBox}>
              <FaExclamationTriangle style={styles.errorIcon} />
              <div style={styles.errorContent}>
                <p style={styles.errorMessage}>Unable to calculate delivery fee</p>
              </div>
              <button 
                onClick={reloadDeliveryFee}
                style={styles.reloadButton}
              >
                <FaSyncAlt style={styles.reloadIcon} />
                Retry
              </button>
            </div>
          )}
        </div>

        {/* Cart Items List */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <FaShoppingCart style={styles.sectionIcon} />
            <h2 style={styles.sectionTitle}>Your Order Items</h2>
          </div>
          
          <div style={styles.receiptContainer}>
            {/* Receipt Header */}
            <div style={styles.receiptHeader}>
              <h3 style={styles.receiptTitle}>LIHEN PHARMACY RECEIPT</h3>
              <p style={styles.receiptSubtitle}>
                {cart.length} item{cart.length !== 1 ? 's' : ''} in total
              </p>
            </div>
            
            {/* Receipt Items Header */}
            <div style={styles.receiptHeaderRow}>
              <div style={styles.receiptHeaderCell}>PRODUCT</div>
              <div style={styles.receiptHeaderCell}>QTY</div>
              <div style={styles.receiptHeaderCell}>PRICE</div>
              <div style={styles.receiptHeaderCell}>TOTAL</div>
            </div>
            
            {/* Receipt Items */}
            <div style={styles.receiptItemsContainer}>
              {cart.map((item, index) => renderReceiptItem(item, index))}
            </div>
          </div>
        </div>

        {/* Delivery Fee Error Message */}
        {deliveryFeeError && (
          <div style={styles.errorBox}>
            <FaExclamationTriangle style={styles.errorIcon} />
            <div style={styles.errorContent}>
              <p style={styles.errorMessage}>{deliveryFeeError}</p>
            </div>
            <button 
              onClick={reloadDeliveryFee}
              disabled={isFetchingDeliveryFee}
              style={styles.reloadButton}
            >
              <FaSyncAlt style={styles.reloadIcon} />
              {isFetchingDeliveryFee ? 'Loading...' : 'Reload'}
            </button>
          </div>
        )}

        {/* Payment Button */}
        <div style={styles.paymentContainer}>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid() || isLoading}
            style={{
              ...styles.payButton,
              backgroundColor: isFormValid() ? "#2962FF" : "#cccccc",
              cursor: isFormValid() ? "pointer" : "not-allowed"
            }}
          >
            <FaLock style={styles.lockIcon} />
            {isLoading ? "Processing Payment..." : 
             deliveryFee !== null ? `Pay ₦${finalAmount.toLocaleString()}` : 
             "Cannot calculate total"}
          </button>
          
          {firebaseError && (
            <p style={styles.errorText}>{firebaseError}</p>
          )}
        </div>
      </div>

      {isLoading && (
        <div style={styles.overlay}>
          <div style={styles.overlayContent}>
            <div style={styles.overlaySpinner}></div>
            <p style={styles.overlayText}>Processing your order...</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced styles with CSS animation fix
const styles = {
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
  },
  // PC Warning Alert Styles
  pcWarning: {
    backgroundColor: "#FFFBEB",
    border: "1px solid #F59E0B",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "20px",
  },
  pcWarningContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  pcWarningIcon: {
    color: "#F59E0B",
    fontSize: "20px",
    flexShrink: 0,
    marginTop: "2px",
  },
  pcWarningText: {
    flex: 1,
    color: "#92400E",
    fontSize: "14px",
    lineHeight: "1.4",
    margin: 0,
  },
  pcWarningClose: {
    background: "none",
    border: "none",
    fontSize: "18px",
    fontWeight: "bold",
    color: "#92400E",
    cursor: "pointer",
    padding: 0,
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyContainer: {
    textAlign: "center",
    padding: "60px 20px",
    maxWidth: "400px",
    margin: "40px auto",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  emptyIcon: {
    fontSize: "80px",
    marginBottom: "16px",
    color: "#cbd5e1",
  },
  emptyTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#374151",
    marginBottom: "8px",
  },
  emptyText: {
    fontSize: "16px",
    color: "#64748B",
    marginBottom: "24px",
  },
  backButton: {
    backgroundColor: "#2962FF",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    marginTop: "20px",
  },
  section: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
  },
  sectionIcon: {
    color: "#2962FF",
    fontSize: "24px",
    marginRight: "10px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1F2937",
    margin: "0",
  },
  deliveryBox: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  inputGroup: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    padding: "0 16px",
    height: "56px",
    border: "1.5px solid #E2E8F0",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    transition: "all 0.2s ease",
  },
  inputGroupFocused: {
    borderColor: "#2962FF",
    boxShadow: "0 0 0 4px rgba(41, 98, 255, 0.1)",
    transform: "translateY(-1px)",
  },
  inputIcon: {
    color: "#94A3B8",
    fontSize: "20px", // Increased from 18px for better visibility
    marginRight: "12px",
    minWidth: "20px", // Ensures consistent sizing
    flexShrink: 0, // Prevents icon from shrinking
  },
  input: {
    flex: 1,
    fontSize: "16px",
    color: "#1F2937",
    fontWeight: "500",
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
  },
  errorText: {
    color: "#EF4444",
    fontSize: "14px",
    margin: "5px 0 0 0",
    textAlign: "center",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
  },
  spinner: {
    width: "24px",
    height: "24px",
    border: "3px solid #E5E7EB",
    borderTop: "3px solid #2962FF",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "10px",
  },
  loadingText: {
    color: "#64748B",
    fontSize: "14px",
    margin: "0",
  },
  distanceContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    backgroundColor: "#F0FDF4",
    borderRadius: "12px",
  },
  distanceIcon: {
    color: "#10B981",
    fontSize: "24px",
    marginRight: "10px",
  },
  distanceText: {
    fontSize: "16px",
    color: "#374151",
    margin: "0",
  },
  distanceValue: {
    fontWeight: "700",
    color: "#10B981",
  },
  retryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    backgroundColor: "#EFF6FF",
    borderRadius: "12px",
    border: "none",
    color: "#2962FF",
    fontWeight: "600",
    fontSize: "16px",
    cursor: "pointer",
  },
  retryIcon: {
    marginRight: "8px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    fontSize: "16px",
  },
  summaryNote: {
    fontSize: "12px",
    color: "#EF4444",
    fontStyle: "italic",
    textAlign: "center",
    marginTop: "4px",
  },
  divider: {
    height: "1px",
    backgroundColor: "#E5E7EB",
    margin: "12px 0",
  },
  finalSummaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "18px",
    fontWeight: "700",
  },
  receiptContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    overflow: "hidden",
  },
  receiptHeader: {
    backgroundColor: "#2962FF",
    padding: "16px",
    textAlign: "center",
  },
  receiptTitle: {
    fontSize: "16px",
    color: "white",
    fontWeight: "700",
    margin: "0 0 4px 0",
  },
  receiptSubtitle: {
    fontSize: "12px",
    color: "rgba(255, 255, 255, 0.8)",
    margin: "0",
  },
  receiptHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px",
    backgroundColor: "#F8FAFC",
    borderBottom: "1px solid #E5E7EB",
    fontWeight: "600",
    fontSize: "12px",
    color: "#64748B",
  },
  receiptHeaderCell: {
    flex: 1,
    textAlign: "center",
  },
  receiptItemsContainer: {
    maxHeight: "200px",
    overflowY: "auto",
  },
  receiptItemRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px",
    borderBottom: "1px solid #F1F5F9",
    fontSize: "14px",
  },
  receiptProductName: {
    flex: 2,
    color: "#374151",
    fontWeight: "500",
    marginRight: "8px",
  },
  receiptQuantity: {
    flex: 1,
    textAlign: "center",
    color: "#64748B",
    fontWeight: "500",
  },
  receiptUnitPrice: {
    flex: 1,
    textAlign: "center",
    color: "#64748B",
    fontWeight: "500",
  },
  receiptTotal: {
    flex: 1,
    textAlign: "center",
    color: "#1F2937",
    fontWeight: "600",
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#FFF3F3",
    border: "1px solid #FF4444",
    borderRadius: "4px",
    padding: "10px",
    marginTop: "10px",
  },
  errorIcon: {
    color: "#FF4444",
    fontSize: "16px",
    marginRight: "10px",
  },
  errorContent: {
    flex: 1,
  },
  errorMessage: {
    color: "#FF4444",
    margin: "0",
    fontSize: "14px",
  },
  reloadButton: {
    display: "flex",
    alignItems: "center",
    background: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    padding: "5px 10px",
    cursor: "pointer",
    fontSize: "14px",
  },
  reloadIcon: {
    marginRight: "5px",
  },
  paymentContainer: {
    marginTop: "20px",
    padding: "20px",
    backgroundColor: "rgba(248, 250, 252, 0.95)",
    borderTop: "1px solid #E5E7EB",
    position: "sticky",
    bottom: "0",
  },
  payButton: {
    width: "100%",
    padding: "16px",
    borderRadius: "16px",
    border: "none",
    fontSize: "18px",
    fontWeight: "700",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "background-color 0.2s ease",
  },
  lockIcon: {
    fontSize: "20px",
  },
  overlay: {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: "1000",
  },
  overlayContent: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  overlaySpinner: {
    width: "32px",
    height: "32px",
    border: "4px solid #E5E7EB",
    borderTop: "4px solid #2962FF",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  overlayText: {
    fontSize: "16px",
    color: "#374151",
    fontWeight: "600",
    margin: "0",
  },
};

// Add CSS animation via style tag
const styleTag = document.createElement('style');
styleTag.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleTag);

export default CheckoutScreen;